import { useMemo, useState, useCallback } from 'react';
import AppLayout from '../components/layout/AppLayout';
import { useCompanyDeadlines } from '../hooks/useCompanyDeadlines';
import { useApp } from '../context/AppContext';
import { useUI } from '../context/UIContext';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/constants';
import { FiAlertTriangle, FiDownload, FiCalendar, FiClock, FiTrendingUp, FiChevronLeft, FiChevronRight, FiX, FiFileText, FiClipboard, FiKey } from 'react-icons/fi';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import StatCard from '../components/common/StatCard';
import PageHeader from '../components/common/PageHeader';
import SegmentedControl from '../components/common/SegmentedControl';

/* ─── Constants ───────────────────────────────────────────── */

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MON3  = MONTHS.map((m) => m.slice(0, 3));
const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const KEY_DATES = [
  ['30 Apr', 'TDS Payment (Mar) / MSME-1 (H2)'],
  ['30 May', 'LLP Form 11 / PAS-6 (H2)'],
  ['31 May', 'TDS Return Q4'],
  ['30 Jun', 'DIR-3 KYC (every 3rd FY)'],
  ['31 Jul', 'ITR (Non-audit)'],
  ['30 Sep', 'AGM deadline'],
  ['30 Oct', 'AOC-4 / LLP Form 8'],
  ['31 Oct', 'ITR (Audit) / MSME-1 (H1)'],
  ['29 Nov', 'MGT-7 / PAS-6 (H1)'],
  ['31 Dec', 'GSTR-9 / GSTR-9C'],
];

const TONE_COLORS = {
  crit: { bg: '#f2e9e6', border: '#9a564a', dot: '#9a564a', text: '#9a564a' },
  warn: { bg: '#f3ebd7', border: '#8a6a1f', dot: '#8a6a1f', text: '#8a6a1f' },
  info: { bg: '#edeef3', border: '#3a4a66', dot: '#3a4a66', text: '#3a4a66' },
  est:  { bg: '#efe9e0', border: '#6f6049', dot: '#6f6049', text: '#6f6049' },
};
const TONE_LABEL = { crit: 'OVERDUE', warn: 'Upcoming', info: 'On Track', est: 'Estimated' };

/* ─── Helpers ─────────────────────────────────────────────── */

function buildCalendar(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);
  return cells;
}

function fmtDate(d) { return `${d.getDate()} ${MON3[d.getMonth()]} ${d.getFullYear()}`; }

/** Group events by formCode + day for the sidebar */
function groupDues(events, year, month) {
  const map = {};
  for (const ev of events) {
    if (ev.year !== year || ev.month !== month) continue;
    const key = `${ev.formCode}::${ev.day}`;
    if (!map[key]) map[key] = { formCode: ev.formCode, formName: ev.formName || ev.formCode, day: ev.day, tone: 'info', companies: [] };
    map[key].companies.push({ name: ev.companyName, cin: ev.companyCin, status: ev.status, daysRemaining: ev.daysRemaining, estimated: ev.estimated || false });
    if (ev.tone === 'crit') map[key].tone = 'crit';
    else if (ev.tone === 'warn' && map[key].tone !== 'crit') map[key].tone = 'warn';
    else if (ev.tone === 'est' && map[key].tone === 'info') map[key].tone = 'est';
  }
  return Object.values(map).sort((a, b) => a.day - b.day || a.formCode.localeCompare(b.formCode));
}

/** Build per-day dueMap for calendar cells */
function buildDueMap(events, year, month) {
  const map = {};
  for (const ev of events) {
    if (ev.year !== year || ev.month !== month) continue;
    if (!map[ev.day]) map[ev.day] = { events: [], tone: 'info', forms: new Set() };
    map[ev.day].events.push(ev);
    map[ev.day].forms.add(ev.formCode);
    if (ev.tone === 'crit') map[ev.day].tone = 'crit';
    else if (ev.tone === 'warn' && map[ev.day].tone !== 'crit') map[ev.day].tone = 'warn';
    else if (ev.tone === 'est' && map[ev.day].tone === 'info') map[ev.day].tone = 'est';
  }
  return map;
}

/* ═══════════════════════════════════════════════════════════ */

export default function CalendarPage() {
  const now = new Date();
  const todayY = now.getFullYear(), todayM = now.getMonth(), todayD = now.getDate();

  /* ── Data (user's own companies) ─────────────────────── */
  const { companies } = useApp();
  const { alerts, calendarEvents } = useCompanyDeadlines();
  const { showToast } = useUI();
  const navigate = useNavigate();

  /* ── Calendar state ───────────────────────────────────── */
  const [year, setYear]   = useState(todayY);
  const [month, setMonth] = useState(todayM);
  const [selectedDay, setSelectedDay] = useState(null);
  const [upcomingYear, setUpcomingYear] = useState('this');

  const cells    = useMemo(() => buildCalendar(year, month), [year, month]);
  const dueMap   = useMemo(() => buildDueMap(calendarEvents, year, month), [calendarEvents, year, month]);
  const monthDues = useMemo(() => groupDues(calendarEvents, year, month), [calendarEvents, year, month]);

  /* ── Selected-day filings ─────────────────────────────── */
  const selectedDayDues = useMemo(() => {
    if (selectedDay == null) return [];
    return groupDues(calendarEvents, year, month).filter((g) => g.day === selectedDay);
  }, [calendarEvents, year, month, selectedDay]);

  /* ── Upcoming filings (this year / next year) ─────────── */
  const upcomingGrouped = useMemo(() => {
    const targetYear = upcomingYear === 'this' ? todayY : todayY + 1;
    const todayDate = new Date(todayY, todayM, todayD);
    const filtered = calendarEvents.filter((ev) => {
      const evDate = new Date(ev.year, ev.month, ev.day);
      return upcomingYear === 'this' ? (evDate >= todayDate && ev.year === targetYear) : ev.year === targetYear;
    });
    const map = {};
    for (const ev of filtered) {
      const key = `${ev.formCode}::${ev.year}-${ev.month}-${ev.day}`;
      if (!map[key]) map[key] = { formCode: ev.formCode, formName: ev.formName || ev.formCode, year: ev.year, month: ev.month, day: ev.day, tone: 'info', estimated: false, companies: [] };
      map[key].companies.push(ev.companyName);
      if (ev.estimated) map[key].estimated = true;
      if (ev.tone === 'crit') map[key].tone = 'crit';
      else if (ev.tone === 'warn' && map[key].tone !== 'crit') map[key].tone = 'warn';
      else if (ev.tone === 'est' && map[key].tone === 'info') map[key].tone = 'est';
    }
    return Object.values(map).sort((a, b) => new Date(a.year, a.month, a.day) - new Date(b.year, b.month, b.day));
  }, [calendarEvents, upcomingYear, todayY, todayM, todayD]);

  /* ── Navigation ───────────────────────────────────────── */
  const prevMonth = useCallback(() => { setSelectedDay(null); if (month === 0) { setMonth(11); setYear((y) => y - 1); } else setMonth((m) => m - 1); }, [month]);
  const nextMonth = useCallback(() => { setSelectedDay(null); if (month === 11) { setMonth(0); setYear((y) => y + 1); } else setMonth((m) => m + 1); }, [month]);
  const goToday   = useCallback(() => { setYear(todayY); setMonth(todayM); setSelectedDay(null); }, [todayY, todayM]);
  const isCurrentMonth = year === todayY && month === todayM;

  /* ── Action Handlers ─────────────────────────────────── */
  const handleViewAlerts = useCallback(() => {
    navigate(ROUTES.ALERTS);
  }, [navigate]);

  const exportCalendar = useCallback(() => {
    // Export calendar events as CSV
    const csvHeaders = ['Date', 'Form Code', 'Company', 'Status', 'Days Remaining', 'Estimated'];
    const csvData = calendarEvents.map(event => [
      `${event.day}-${event.month + 1}-${event.year}`,
      event.formCode,
      event.companyName,
      event.status,
      event.daysRemaining,
      event.estimated ? 'Yes' : 'No'
    ]);
    
    // CSV-escape: double internal quotes, and neutralize spreadsheet formula
    // injection by prefixing cells that start with = + - @ (or control chars).
    const csvCell = (val) => {
      let s = String(val ?? '');
      if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csvContent = [csvHeaders, ...csvData]
      .map(row => row.map(csvCell).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-calendar-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    showToast('Calendar exported successfully', 'success');
  }, [calendarEvents, showToast]);

  /* ═════════════════════════════════════════════════════════ */

  // Calculate alert counts for summary
  const overdueCount = useMemo(() => alerts.filter((a) => (a.daysRemaining ?? 0) < 0).length, [alerts]);
  const dueSoonCount = useMemo(() => alerts.filter((a) => (a.daysRemaining ?? 0) >= 0 && (a.daysRemaining ?? 0) <= 7).length, [alerts]);
  const totalPenalty = useMemo(() => alerts.reduce((sum, a) => sum + (a.penalty || 0), 0), [alerts]);

  return (
    <AppLayout pageTitle="Due Calendar" hasDashboardAlert={overdueCount > 0}>

      <style>{`
        .cal-cell:hover { transform: translateY(-1px); box-shadow: var(--sh-2); border-color: var(--teal) !important; }
        .cal-cell.selected { box-shadow: var(--sh-focus-soft); }
      `}</style>

      {/* ─── Page Header ──────────────────────────────────── */}
      <PageHeader
        title={<>Compliance Due Calendar &mdash; {MONTHS[month]} {year}</>}
        subtitle={companies.length > 0
          ? `All company types · ${companies.length} companies · ${calendarEvents.length} upcoming deadlines · Click a date to see due filings`
          : 'Add companies to your portfolio to see company-specific deadlines on the calendar'}
        actions={(
          <>
            <Button variant="outline" size="sm" leftIcon={<FiAlertTriangle size={15} />} onClick={handleViewAlerts}>
              View Alerts
            </Button>
            <Button variant="gold" size="sm" leftIcon={<FiDownload size={15} />} onClick={exportCalendar} disabled={calendarEvents.length === 0}>
              Export
            </Button>
          </>
        )}
      />

      {/* ─── Summary Section ───────────────────────────────── */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total Alerts" value={alerts.length} sub="Active filings" tone="#3a4a66" icon={FiCalendar} />
        <StatCard label="Overdue" value={overdueCount} sub="Require immediate action" tone="#9a564a" icon={FiAlertTriangle} />
        <StatCard label="Due This Week" value={dueSoonCount} sub="Within 7 days" tone="#8a6a1f" icon={FiClock} />
        <StatCard
          label="Total Penalty"
          value={`₹${totalPenalty.toLocaleString('en-IN')}`}
          sub={totalPenalty > 0 ? 'Estimated exposure' : 'All clear'}
          tone={totalPenalty > 0 ? '#9a564a' : '#4a6b3f'}
          icon={FiTrendingUp}
          compact
        />
      </div>

      {/* ─── Main Grid: Calendar (2fr) + Sidebar (1fr) ────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 18, marginBottom: 22, alignItems: 'start' }}>

        {/* ══════ LEFT COLUMN (Calendar + Upcoming) ═══════════ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Calendar Card */}
        <Card padded={false} className="overflow-hidden">
          {/* Header */}
          <div className="px-5 py-4 border-b flex items-center gap-2.5 flex-wrap" style={{ borderColor: 'rgba(226,217,200,0.55)' }}>
            <div className="flex-1 flex items-center gap-2 text-[16px] font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
              <FiCalendar size={15} style={{ color: '#145f5f' }} /> {MONTHS[month]} {year}
            </div>
            {!isCurrentMonth && (
              <Button variant="primary" size="xs" onClick={goToday}>Today</Button>
            )}
            <Button variant="outline" size="xs" leftIcon={<FiChevronLeft size={13} />} onClick={prevMonth}>
              {MON3[(month + 11) % 12]}
            </Button>
            <Button variant="outline" size="xs" rightIcon={<FiChevronRight size={13} />} onClick={nextMonth}>
              {MON3[(month + 1) % 12]}
            </Button>
          </div>

          {/* Grid */}
          <div style={{ padding: '18px 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
              {WEEKDAYS.map((d) => (
                <div key={d} style={{ fontSize: 10, fontWeight: 700, textAlign: 'center', padding: '6px 0', color: '#7a7060', textTransform: 'uppercase' }}>{d}</div>
              ))}
              {cells.map((day, idx) => {
                if (!day) return <div key={`e${idx}`} style={{ minHeight: 52, borderRadius: 7, background: '#f0ece2' }} />;
                const due = dueMap[day];
                const isToday = todayY === year && todayM === month && todayD === day;
                const isSelected = selectedDay === day;
                const tone = due?.tone || null;
                const isCrit = tone === 'crit';
                const isWarn = tone === 'warn';
                const isEst  = tone === 'est';
                const forms = due ? [...due.forms] : [];
                return (
                  <button
                    key={`${month}-${day}`}
                    className={`cal-cell${isSelected ? ' selected' : ''}`}
                    onClick={() => setSelectedDay(isSelected ? null : day)}
                    style={{
                      minHeight: 52, borderRadius: 7, padding: 5, textAlign: 'left', position: 'relative',
                      border: '1px solid', cursor: due ? 'pointer' : 'default', transition: 'all .15s',
                      background: isCrit ? '#f2e9e6' : isEst ? '#efe9e0' : isToday ? '#e3f0ed' : '#f8f5ef',
                      borderColor: isSelected ? '#145f5f' : isCrit ? '#9a564a' : isWarn ? '#8a6a1f' : isEst ? '#6f6049' : isToday ? '#145f5f' : '#ddd6c6',
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 700, color: isToday ? '#145f5f' : '#08111f' }}>{day}</div>
                    {isToday && <div style={{ fontSize: 8, color: '#145f5f', fontWeight: 800 }}>Today</div>}
                    {forms.length > 0 && (
                      <div style={{ marginTop: 2 }}>
                        {forms.slice(0, 2).map((f) => (
                          <div key={f} style={{ fontSize: 8, lineHeight: '12px', display: 'flex', alignItems: 'center', gap: 2 }}>
                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: TONE_COLORS[tone]?.dot || '#3a4a66', flexShrink: 0 }} />
                            {f}
                          </div>
                        ))}
                        {forms.length > 2 && <div style={{ fontSize: 8, color: '#7a7060' }}>+{forms.length - 2} more</div>}
                      </div>
                    )}
                    {due && due.events.length > 1 && (
                      <div style={{ position: 'absolute', top: 2, right: 2, width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: '#fff', background: TONE_COLORS[tone]?.dot || '#8a6a1f' }}>
                        {due.events.length}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </Card>

        {/* Upcoming Filings — below calendar */}
        <Card padded={false} className="overflow-hidden">
          <div className="px-5 py-3.5 border-b flex items-center gap-2 flex-wrap" style={{ borderColor: 'rgba(226,217,200,0.55)' }}>
            <div className="flex-1 flex items-center gap-2 text-[16px] font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
              <FiClock size={15} style={{ color: '#8a6a1f' }} /> Upcoming Filings
            </div>
            <SegmentedControl
              options={[{ key: 'this', label: String(todayY) }, { key: 'next', label: String(todayY + 1) }]}
              value={upcomingYear}
              onChange={setUpcomingYear}
            />
          </div>
          <div style={{ padding: '10px 20px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {upcomingGrouped.length > 0 ? upcomingGrouped.map((g) => {
              const d = new Date(g.year, g.month, g.day);
              const tc = TONE_COLORS[g.tone] || TONE_COLORS.info;
              return (
                <div key={`${g.formCode}-${g.year}-${g.month}-${g.day}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f0ece2' }}>
                  <div style={{ width: 70, fontSize: 11, color: '#7a7060', flexShrink: 0 }}>{fmtDate(d).replace(` ${g.year}`, '')}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#333' }}>{g.formCode}</div>
                    <div style={{ fontSize: 10, color: '#7a7060', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: '14px', maxHeight: 28 }}>
                      {g.companies.join(', ')}
                    </div>
                  </div>
                  <span style={{ display: 'inline-flex', padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700, flexShrink: 0, background: tc.bg, color: tc.text }}>
                    {TONE_LABEL[g.tone]}
                  </span>
                </div>
              );
            }) : (
              <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 12, color: '#7a7060' }}>
                No upcoming filings for {upcomingYear === 'this' ? todayY : todayY + 1}
              </div>
            )}
          </div>
        </Card>

        </div>{/* end left column */}

        {/* ══════ SIDEBAR ═══════════════════════════════════ */}
        <div className="flex flex-col gap-3.5">

          {/* Selected Date Detail */}
          {selectedDay != null && (
            <Card padded={false} className="overflow-hidden" style={{ border: '1.5px solid #145f5f', boxShadow: '0 2px 16px rgba(20,95,95,.12)' }}>
              <div style={{ padding: '10px 16px', background: '#e4f5f3', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="flex items-center gap-1.5" style={{ fontSize: 13, fontWeight: 700, color: '#145f5f' }}>
                  <FiFileText size={13} /> {selectedDay} {MON3[month]} {year}
                </div>
                <button onClick={() => setSelectedDay(null)} aria-label="Close" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#145f5f', display: 'flex' }}>
                  <FiX size={15} />
                </button>
              </div>
              <div style={{ padding: '10px 16px' }}>
                {selectedDayDues.length > 0 ? selectedDayDues.map((g) => {
                  const tc = TONE_COLORS[g.tone] || TONE_COLORS.info;
                  return (
                    <div key={g.formCode} style={{ marginBottom: 8, padding: '8px 10px', borderRadius: 9, background: tc.bg, border: `1px solid ${tc.border}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{g.formCode} <span style={{ fontWeight: 400, color: '#7a7060' }}>({g.companies.length})</span></span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: tc.text }}>{TONE_LABEL[g.tone]}</span>
                      </div>
                      <div style={{ fontSize: 10, color: '#555', marginTop: 3 }}>
                        {g.companies.map((c) => c.name).join(', ')}
                      </div>
                    </div>
                  );
                }) : (
                  <div style={{ fontSize: 12, color: '#7a7060', textAlign: 'center', padding: '8px 0' }}>No filings due on this date</div>
                )}
              </div>
            </Card>
          )}

          {/* This Month's Dues */}
          <Card padded={false} className="overflow-hidden">
            <div className="px-5 py-4 border-b" style={{ borderColor: 'rgba(226,217,200,0.55)' }}>
              <div className="flex items-center gap-2 text-[16px] font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
                <FiClipboard size={15} style={{ color: '#3a4a66' }} /> This Month&apos;s Dues {monthDues.length > 0 && <span style={{ fontSize: 12, fontWeight: 400, color: '#7a7060' }}>({monthDues.length})</span>}
              </div>
            </div>
            <div style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {monthDues.length > 0 ? monthDues.map((g) => {
                const tc = TONE_COLORS[g.tone] || TONE_COLORS.info;
                return (
                  <div key={`${g.formCode}-${g.day}`} style={{ padding: '10px 14px', borderRadius: 10, background: tc.bg, border: `1px solid ${tc.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontSize: 12.5, fontWeight: 700 }}>
                          {g.formCode}
                          {g.companies.length > 1 && <span style={{ fontWeight: 400, color: '#7a7060' }}> ({g.companies.length} companies)</span>}
                        </div>
                        <div style={{ fontSize: 11, color: '#7a7060', marginTop: 2 }}>{g.day} {MON3[month]} {year}</div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: tc.text, whiteSpace: 'nowrap' }}>{TONE_LABEL[g.tone]}</span>
                    </div>
                    <div style={{ fontSize: 10, color: '#555', marginTop: 4, lineHeight: '16px' }}>
                      {g.companies.length <= 3
                        ? g.companies.map((c) => c.name).join(', ')
                        : `${g.companies.slice(0, 2).map((c) => c.name).join(', ')} + ${g.companies.length - 2} more`}
                    </div>
                  </div>
                );
              }) : (
                <div style={{ fontSize: 12, color: '#7a7060', textAlign: 'center', padding: '16px 0' }}>No deadlines this month</div>
              )}
            </div>
          </Card>

          {/* Key Annual Dates */}
          <Card padded={false} className="overflow-hidden">
            <div className="px-5 py-4 border-b" style={{ borderColor: 'rgba(226,217,200,0.55)' }}>
              <div className="flex items-center gap-2 text-[16px] font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
                <FiKey size={15} style={{ color: '#b8973a' }} /> Key Annual Dates
              </div>
            </div>
            <div style={{ padding: '14px 20px', fontSize: 12 }}>
              {KEY_DATES.map(([date, item]) => (
                <div key={date} style={{ display: 'flex', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f0ece2' }}>
                  <div style={{ width: 64, color: '#7a7060', flexShrink: 0 }}>{date}</div>
                  <div style={{ fontWeight: 600, color: '#08111f' }}>{item}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
